import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { Input, Label } from "@calcom/ui/components/form";
import { InfoIcon } from "@coss/ui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import useDigitInput from "react-digit-input";
import { createPortal } from "react-dom";

/**
 * Deliberately NOT built on Radix Dialog.
 *
 * The shared Radix `Dialog`/`DialogContent` overlay (animated via Presence +
 * an aria-hidden layer manager) could be orphaned in production — left in the
 * DOM as `<div data-state="open" ... pointer-events: auto>` covering the page
 * and swallowing every click — when this dialog closed while the booking flow
 * unmounted/navigated underneath it. We chased that through several Radix-level
 * fixes without fully killing it.
 *
 * This is a plain portal + conditional render: when `isOpenDialog` is false the
 * component returns null, so React removes the whole overlay subtree
 * synchronously. There is no Presence, no exit animation to wait on, and no
 * aria-hidden manager — so an orphaned overlay cannot exist by construction.
 * Closing is driven only by the explicit buttons.
 */
export const EmailConfirmAndVerifyDialog = ({
  isOpenDialog,
  phase,
  email,
  onConfirm,
  isConfirming,
  onEdit,
  onClose,
  willSendVerificationCode,
  isUserSessionRequiredToVerify = true,
  verifyCodeWithSessionNotRequired,
  verifyCodeWithSessionRequired,
  resetErrors,
  setIsPending,
  isPending,
  error,
}: {
  isOpenDialog: boolean;
  phase: "confirm" | "code";
  email: string;
  onConfirm: () => void;
  isConfirming: boolean;
  onEdit: () => void;
  onClose: () => void;
  willSendVerificationCode: boolean;
  isUserSessionRequiredToVerify?: boolean;
  verifyCodeWithSessionNotRequired: (code: string, email: string) => void;
  verifyCodeWithSessionRequired: (code: string, email: string) => void;
  resetErrors: () => void;
  isPending: boolean;
  setIsPending: (status: boolean) => void;
  error: string;
}) => {
  const { t } = useLocale();
  const [value, setValue] = useState("");
  const [hasVerified, setHasVerified] = useState(false);
  const setVerificationCode = useBookerStoreContext((state) => state.setVerificationCode);
  const overlayRef = useRef<HTMLDivElement>(null);

  const digits = useDigitInput({
    acceptedCharacters: /^[0-9]$/,
    length: 6,
    value,
    onChange: useCallback(
      (newValue: string) => {
        resetErrors();
        setValue(newValue);
      },
      [resetErrors]
    ),
  });

  const verifyCode = useCallback(() => {
    resetErrors();
    setIsPending(true);
    if (isUserSessionRequiredToVerify) {
      verifyCodeWithSessionRequired(value, email);
    } else {
      verifyCodeWithSessionNotRequired(value, email);
    }
    setVerificationCode(value);
    setHasVerified(true);
  }, [
    resetErrors,
    setIsPending,
    isUserSessionRequiredToVerify,
    verifyCodeWithSessionRequired,
    value,
    email,
    verifyCodeWithSessionNotRequired,
    setVerificationCode,
  ]);

  useEffect(() => {
    if (phase !== "code" || hasVerified || error || isPending || !/^\d{6}$/.test(value.trim())) return;
    verifyCode();
  }, [phase, error, isPending, value, hasVerified, verifyCode]);

  // Reset the entered code whenever the code step is (re)entered.
  useEffect(() => {
    setValue("");
    setHasVerified(false);
  }, [phase, isOpenDialog]);

  // On mobile the booking form itself lives inside a Radix Dialog, whose FocusScope
  // traps focus: it listens for `focusin`/`focusout` on document and yanks focus back
  // into its own subtree whenever focus lands anywhere else. This dialog portals to
  // <body>, i.e. outside that subtree, so every attempt to focus a code input was
  // immediately undone — the phone keyboard never opened and pasting had no target.
  // Swallow those two events in the capture phase (before they reach Radix's listener
  // on document) whenever they concern our own layer; the trap stays fully intact for
  // everything else and resumes untouched once this dialog closes.
  useEffect(() => {
    if (!isOpenDialog) return;

    const isInsideDialog = (node: EventTarget | null) =>
      node instanceof Node && Boolean(overlayRef.current?.contains(node));

    const allowFocusOnThisLayer = (event: FocusEvent) => {
      if (isInsideDialog(event.target) || isInsideDialog(event.relatedTarget)) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener("focusin", allowFocusOnThisLayer, true);
    document.addEventListener("focusout", allowFocusOnThisLayer, true);
    return () => {
      document.removeEventListener("focusin", allowFocusOnThisLayer, true);
      document.removeEventListener("focusout", allowFocusOnThisLayer, true);
    };
  }, [isOpenDialog]);

  if (!isOpenDialog || typeof document === "undefined") return null;

  const digitClassName = "h-12 w-12 text-xl! text-center";

  return createPortal(
    // pointer-events-auto: on mobile this modal can open while a Radix Dialog
    // (the booking form / slot-selection modal) is still mounted. That modal sets
    // `pointer-events: none` on <body>, which this portaled subtree inherits —
    // making every button dead to the touch. Re-enabling pointer events on our
    // own layer restores clicks.
    //
    // stopImmediatePropagation on pointerdown: this dialog portals to <body>, i.e.
    // outside the React subtree of the underlying Radix Dialog. Radix's
    // DismissableLayer listens for `pointerdown` on document and treats anything
    // outside its tree as an outside-click, dismissing the dialog (which clears
    // the selected timeslot and drops the user back to the calendar). Stopping the
    // native event before it reaches document keeps the underlying form open while
    // the booker edits/confirms here. React still dispatches our own button
    // handlers, so the buttons keep working.
    <div
      ref={overlayRef}
      className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center p-4"
      onPointerDown={(e) => e.nativeEvent.stopImmediatePropagation()}>
      <div className="fadeIn bg-neutral-800/70 dark:bg-neutral-800/80 absolute inset-0" aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className="bg-default fadeIn relative z-[101] w-full max-w-md rounded-2xl px-8 pt-8 text-left shadow-xl">
        {phase === "confirm" ? (
          <>
            <div className="mb-4">
              <h2 className="text-semibold text-emphasis font-cal mb-1 text-xl">{t("confirm_your_email")}</h2>
              <p className="text-subtle text-sm">
                {willSendVerificationCode
                  ? t("confirm_email_before_sending_code")
                  : t("confirm_email_before_booking")}
              </p>
            </div>
            <p
              className="text-emphasis break-all text-center text-2xl font-semibold"
              data-testid="confirm-email-address">
              {email}
            </p>
            <div className="bg-cal-muted border-muted -mx-8 mt-10 flex justify-end gap-x-2 rounded-b-2xl border px-8 py-4">
              <Button type="button" color="minimal" onClick={onEdit} data-testid="confirm-email-edit">
                {t("edit")}
              </Button>
              <Button
                type="button"
                onClick={onConfirm}
                loading={isConfirming}
                data-testid="confirm-email-button">
                {t("confirm")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4">
              <h2 className="text-semibold text-emphasis font-cal mb-1 text-xl">{t("verify_your_email")}</h2>
              <p className="text-subtle text-sm">{t("enter_digit_code", { email })}</p>
            </div>
            <Label htmlFor="code">{t("code")}</Label>
            <div className="flex flex-row justify-between">
              {digits.map((digit, index) => (
                <Input
                  // biome-ignore lint/suspicious/noArrayIndexKey: the six digit inputs are a fixed, never-reordered list
                  key={index}
                  className={digitClassName}
                  name={`2fa${index + 1}`}
                  type="text"
                  // inputMode="numeric" + pattern="[0-9]*" is what actually gets a
                  // digits-only keypad on a phone: "decimal" adds a separator key on
                  // Android and the pattern is what iOS looks at on a text input.
                  inputMode="numeric"
                  pattern="[0-9]*"
                  {...digit}
                  autoFocus={index === 0}
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                />
              ))}
            </div>
            {error && (
              <div className="mt-2 flex items-center gap-x-2 text-sm text-red-700">
                <div>
                  <InfoIcon className="h-3 w-3" />
                </div>
                <p>{error}</p>
              </div>
            )}
            <div className="bg-cal-muted border-muted -mx-8 mt-10 flex justify-end gap-x-2 rounded-b-2xl border px-8 py-4">
              <Button type="button" color="minimal" onClick={onClose} data-testid="verify-email-close">
                {t("close")}
              </Button>
              <Button type="submit" onClick={verifyCode} loading={isPending}>
                {t("submit")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
